// signals-web.ts — 1:1 JS port of src-tauri/src/signals.rs: the CollectionSignals engine.
// 🔴 WHY: compute_signals was a zero shell on web (fleet zero-stub sweep 2026-08-20) — the
// collection recap had nothing to narrate. Where this and the Rust could drift, the Rust wins.
// Hardened per the Oracle lesson (?? guards).
//
// THE FENCE TRAVELS: observational aggregates over the user's own logged collection —
// counts, points sums, recipe frequencies, WIP ages, paint distributions. Never advice,
// never a purchase recommendation, never a judgement. Notes/photo refs never enter.

import { STATUSES } from "./store";
import type { Faction, Unit, Recipe, Paint } from "./store";

const s = (v: unknown) => String(v ?? "");
const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const round1 = (x: number) => Math.round(x * 10) / 10;
const pct = (part: number, whole: number) => (whole <= 0 ? null : round1((part * 100) / whole));
const parseDay = (d: unknown) => (/^\d{4}-\d{2}-\d{2}/.test(s(d)) ? s(d).slice(0, 10) : null);
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

function toSortedNameCounts(m: Map<string, number>) {
  return [...m.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

export function computeSignalsWeb(factions: Faction[], units: Unit[], recipes: Recipe[], paints: Paint[], today: string) {
  const totalModels = units.reduce((a, u) => a + n(u.model_count), 0);
  const totalBottles = paints.reduce((a, p) => a + n(p.qty), 0);
  const totals = {
    factions: factions.length,
    units: units.length,
    models: totalModels,
    recipes: recipes.length,
    paints: paints.length,
    paint_bottles: totalBottles,
  };

  const totalPoints = units.reduce((a, u) => a + n(u.points), 0);
  const paintedPoints = units.filter((u) => s(u.status) === "painted").reduce((a, u) => a + n(u.points), 0);
  const points_by_status: { status: string; points: number; units: number; models: number }[] = [];
  const status_counts: { status: string; units: number; models: number; pct_of_models: number | null }[] = [];
  for (const st of STATUSES) {
    const inSt = units.filter((u) => s(u.status) === st);
    const pts = inSt.reduce((a, u) => a + n(u.points), 0);
    const mdls = inSt.reduce((a, u) => a + n(u.model_count), 0);
    points_by_status.push({ status: st, points: pts, units: inSt.length, models: mdls });
    status_counts.push({ status: st, units: inSt.length, models: mdls, pct_of_models: pct(mdls, totalModels) });
  }
  const points = {
    total: totalPoints,
    painted: paintedPoints,
    unpainted: totalPoints - paintedPoints,
    painted_pct: pct(paintedPoints, totalPoints),
    by_status: points_by_status,
  };

  const faction_stats = factions
    .map((f) => {
      const fu = units.filter((u) => s(u.faction_id) === s(f.id));
      const tp = fu.reduce((a, u) => a + n(u.points), 0);
      const pp = fu.filter((u) => s(u.status) === "painted").reduce((a, u) => a + n(u.points), 0);
      return {
        name: s(f.name),
        game_system: s(f.game_system),
        units: fu.length,
        models: fu.reduce((a, u) => a + n(u.model_count), 0),
        total_points: tp,
        painted_points: pp,
        painted_pct: pct(pp, tp),
      };
    })
    .sort((a, b) => b.total_points - a.total_points);

  const useByRecipe = new Map<string, { units: number; models: number }>();
  for (const u of units) {
    const rid = u.recipe_id != null ? s(u.recipe_id) : null;
    if (!rid) continue;
    const e = useByRecipe.get(rid) ?? { units: 0, models: 0 };
    e.units += 1;
    e.models += n(u.model_count);
    useByRecipe.set(rid, e);
  }
  const recipe_stats = recipes
    .map((r) => {
      const use = useByRecipe.get(s(r.id)) ?? { units: 0, models: 0 };
      return {
        name: s(r.name),
        step_count: (r.steps ?? []).length,
        units_using: use.units,
        models_using: use.models,
      };
    })
    .sort((a, b) => b.units_using - a.units_using || b.step_count - a.step_count)
    .slice(0, 10);

  // ── WIP ages ──
  const wipUnits = units.filter((u) => s(u.status) === "wip");
  const wipItems: { name: string; days: number; since: string }[] = [];
  const ages: number[] = [];
  for (const u of wipUnits) {
    const since = u.wip_since != null ? parseDay(u.wip_since) : null;
    if (since != null) {
      const days = Math.max(dayDiff(since, today), 0);
      ages.push(days);
      wipItems.push({ name: s(u.name), days, since });
    }
  }
  wipItems.sort((a, b) => b.days - a.days);
  const wip = {
    count: wipUnits.length,
    oldest_days: wipItems[0]?.days ?? null,
    over_year: ages.filter((d) => d > 365).length,
    avg_days: ages.length ? round1(ages.reduce((a, b) => a + b, 0) / ages.length) : null,
    items: wipItems.slice(0, 8),
  };

  const byColor = new Map<string, number>();
  const byBrand = new Map<string, number>();
  for (const p of paints) {
    const cf = s(p.color_family);
    if (cf) byColor.set(cf, (byColor.get(cf) ?? 0) + Math.max(n(p.qty), 0));
    const b = s(p.brand);
    if (b) byBrand.set(b, (byBrand.get(b) ?? 0) + 1);
  }
  const paints_stat = {
    distinct: paints.length,
    bottles: totalBottles,
    by_color_family: toSortedNameCounts(byColor).slice(0, 10),
    by_brand: toSortedNameCounts(byBrand).slice(0, 10),
  };

  return {
    schema:
      "oriora.muster.v1-signals: derived aggregates only (no raw notes / photo refs). Counts, points sums, recipe use-frequencies, WIP ages (days), and paint distributions. 'painted' points = units with status exactly 'painted'; 'unpainted' = everything else. Every figure is a plain observation about the user's own logged collection — describe the numbers, never advise, never recommend a purchase, never judge.",
    generated_for: today,
    totals,
    points,
    status_counts,
    factions: faction_stats,
    recipes: recipe_stats,
    wip,
    paints: paints_stat,
  };
}
